import React, {useMemo, useCallback, useState, useEffect, useContext} from 'react';
import {List, Map, Set} from 'immutable';
import {LoadingSpinner} from '@albert-io/atomic';
import {debounce} from 'lodash';
import {useTracking} from 'react-tracking';

import {GuideModelV1} from 'resources/augmented/Guide/GuideModel.v1';
import {SubjectModelV2} from 'resources/augmented/Subject/SubjectModel.v2';
import {ContentDiscoveryContext} from 'client/components/ContentDiscovery/ContentDiscoveryProvider.react';
import sessionStore from 'client/Session/SessionStore';
import {getSearchResults} from './TopicsModal.queries';
import {
  GuideLevelMapType,
  SortedGuideLevelsType,
  createGuideLevelMap,
  toggleExpandGuideLevel,
  toggleSelectedGuideLevel,
  selectMatchingParent,
  childIsMatch,
  getGuideLineage
} from './GuideLevel.helpers';
import CheckboxOption, {ExpandableResultMemo} from './CheckboxOption.react';

interface SearchResultsProps {
  keyword?: string;
  selectedTopicsModalSubjects: Set<SubjectModelV2>;
  activeGuideType: string;
  guideCollection: Map<string, GuideLevelMapType>;
  setGuideCollection: (guideCollection) => void;
}

const SearchResults = ({
  keyword = '',
  selectedTopicsModalSubjects,
  activeGuideType,
  guideCollection,
  setGuideCollection
}: SearchResultsProps) => {
  // results = list of guides that have their subject included on the resource
  const [results, setResults] = useState<List<any> | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const {
    selectedGuideLevels,
    questionDifficulties,
    questionTypes,
    standardCollection,
    hideAssessmentQuestions,
    hidePreviouslyAssigned,
    getSelectedStandardsAndSets
  } = useContext(ContentDiscoveryContext);

  const {selectedStandardIds, selectedStandardSetIds} =
    getSelectedStandardsAndSets(standardCollection);

  const fetchResults = useCallback(async () => {
    setIsLoading(true);

    const subjects = selectedTopicsModalSubjects.map((subject) => subject.getId()).join(',');
    const guideList = await getSearchResults(
      subjects,
      keyword,
      activeGuideType,
      undefined,
      questionDifficulties,
      questionTypes,
      selectedStandardIds,
      selectedStandardSetIds,
      hideAssessmentQuestions,
      hidePreviouslyAssigned
    );
    let newGuideCollection = guideCollection;
    guideList.forEach((guide) => {
      const blankGuideLevelMap = createGuideLevelMap(guide.getSortedGuideLevels()).toMap();
      let updatedGuideLevelMap = blankGuideLevelMap;
      const guideTree = guide.getMeta().getStructure();
      selectedGuideLevels.forEach((selectedGuideLevel) => {
        updatedGuideLevelMap = toggleSelectedGuideLevel(
          selectedGuideLevel.getId(),
          undefined,
          updatedGuideLevelMap,
          guideTree
        );
      });
      newGuideCollection = newGuideCollection.set(guide.getId(), updatedGuideLevelMap);
    });

    setGuideCollection(newGuideCollection);
    setResults(guideList);
    setIsLoading(false);
  }, [
    keyword,
    selectedTopicsModalSubjects,
    activeGuideType,
    questionDifficulties,
    questionTypes,
    standardCollection,
    hideAssessmentQuestions,
    hidePreviouslyAssigned
  ]);

  const debouncedFetch = useMemo(() => debounce(fetchResults, 300), [fetchResults]);

  useEffect(() => {
    debouncedFetch();
    return () => {
      debouncedFetch.cancel();
    };
  }, [debouncedFetch]);

  const hasKeywordSearch = useMemo(() => !!keyword.length, [keyword]);

  const guideLevelsReturned = results?.some(
    (result) => !result.getGuideLevels().getCollection().isEmpty()
  );

  if (results === null || results.isEmpty() || !guideLevelsReturned)
    return (
      <div className='cd-modal__results-container'>
        {isLoading && (
          <div className='cd-modal__loading-container'>
            <LoadingSpinner size={2} />
          </div>
        )}
        {!isLoading && <div className='cd-modal__loading-container'>No results</div>}
      </div>
    );

  return (
    <div className='cd-modal__results-container'>
      {isLoading && (
        <div className='cd-modal__loading-container'>
          <LoadingSpinner size={2} />
        </div>
      )}
      {guideCollection
        .map((guideLevelMap, guideId) => {
          const guide = results.find((g) => g.getId() === guideId);
          if (guideLevelMap && !guideLevelMap.isEmpty() && guideId && guide) {
            return (
              <GuideRenderer
                guide={guide}
                expandSubject={selectedTopicsModalSubjects.size === 1}
                hasKeywordSearch={hasKeywordSearch}
                guideLevelMap={guideLevelMap}
                setGuideLevelMap={(newGuideLevelMap) => {
                  const newCollection = guideCollection.update(guideId, () => newGuideLevelMap);
                  setGuideCollection(newCollection);
                }}
              />
            );
          }
          return null;
        })
        .toList()}
    </div>
  );
};

export default SearchResults;

interface GuideRendererProps {
  guide: GuideModelV1;
  guideLevelMap: GuideLevelMapType;
  setGuideLevelMap: (GuideLevelMapType) => void;
  hasKeywordSearch: boolean;
  expandSubject: boolean;
}

const GuideRenderer = ({
  guide,
  hasKeywordSearch,
  guideLevelMap,
  setGuideLevelMap,
  expandSubject
}: GuideRendererProps) => {
  const [subjectIsSelected, setSubjectIsSelected] = useState(false);
  const [subjectIsIndeterminate, setSubjectIsIndeterminate] = useState(false);
  const {trackEvent} = useTracking({
    type: 'checkbox',
    event: 'click'
  });

  const {selectedSubjects, setSelectedSubjects} = useContext(ContentDiscoveryContext);

  // checks subject selection state
  useEffect(() => {
    // All subject themes selected = everything is selected
    if (guide) {
      const selected = !guide
        .getMeta()
        .getStructure()
        .keySeq()
        .some((guideThemeId) => {
          const theme = guideLevelMap.get(guideThemeId);
          return !theme.isSelected || theme.isIndeterminate;
        });
      // Not all themes are selected but at least one element in the subject is
      const indeterminate =
        !selected &&
        guide
          .getMeta()
          .getStructure()
          .keySeq()
          .some((guideThemeId) => {
            const theme = guideLevelMap.get(guideThemeId);
            return theme.isSelected || theme.isIndeterminate;
          });
      if (selected) {
        const newSelectedSubjects = selectedSubjects.push(guide.getSubject());
        setSelectedSubjects(newSelectedSubjects);
      } else if (
        !selected &&
        selectedSubjects.some((subject) => subject.getId() === guide.getSubject().getId())
      ) {
        const newSelectedSubjects = selectedSubjects.filter(
          (subject) => subject.getId() !== guide.getSubject().getId()
        );
        setSelectedSubjects(newSelectedSubjects);
      }
      setSubjectIsSelected(selected);
      setSubjectIsIndeterminate(indeterminate);
    }
  }, [guideLevelMap, guide]);

  if (!guide) {
    return null;
  }
  const subject = guide.getSubject();
  const guideTree = guide.getMeta().getStructure();

  if (!subject || !guideTree || subject.isEmpty()) {
    return null;
  }

  const getGuideMatchCount = () => {
    return guideLevelMap.toArray().reduce((count, guideLevelObj) => {
      if (guideLevelObj.guideLevel.getMeta().isMatchesSearch()) {
        return count + 1;
      }
      return count;
    }, 0);
  };

  const matchCount = getGuideMatchCount();

  const shouldRenderItem = (id) => {
    if (!hasKeywordSearch) {
      return true;
    }
    const guideLevelObj = guideLevelMap.get(id);
    const isMatch = guideLevelObj.guideLevel.getMeta().isMatchesSearch();

    return isMatch || childIsMatch(id, guideLevelMap, guideTree) || ancestorIsMatch(id);
  };

  const ancestorIsMatch = (id) => {
    const lineage = getGuideLineage(id, guideLevelMap);
    // remove self
    lineage.pop();
    return (
      lineage &&
      lineage.some((levelId) => guideLevelMap.get(levelId).guideLevel.getMeta().isMatchesSearch())
    );
  };

  const handleKeywordSearchSelection = (selectedElmId, guideLevelMap) => {
    return selectMatchingParent(selectedElmId, guideTree, guideLevelMap);
  };

  const guideQuestionCount = () => {
    return guide.getSortedGuideLevels().reduce((totalQuestionCount: number, guideLevel) => {
      if (guideLevel.getNlevel() === 1) {
        return totalQuestionCount + (guideLevel.getMeta().getContentDiscoveryQuestionCount() || 0);
      }
      return totalQuestionCount;
    }, 0);
  };

  const renderGuideLevels = (sortedGuideLevels: SortedGuideLevelsType) => {
    // @ts-ignore -- @todo: type the reducer
    return sortedGuideLevels.reduce((acc, guideLevel) => {
      const levelId = guideLevel.getId();
      const lineage = getGuideLineage(levelId, guideLevelMap);
      const isParentThemeExpanded = guideLevelMap.get(lineage[0]).isExpanded;
      const isTheme = guideLevel.getNlevel() === 1;

      if (!shouldRenderItem(levelId) || (!isParentThemeExpanded && !isTheme)) {
        return acc;
      }
      const expandedVal = isTheme ? guideLevelMap.get(levelId).isExpanded : null;
      // @ts-ignore -- typing the accumulator
      return acc.push(
        <CheckboxOption
          key={guideLevel.getId()}
          checked={guideLevelMap.get(levelId).isSelected}
          id={guideLevel.getId()}
          className={!isTheme ? `u-mar-l_${guideLevel.getNlevel() * 2 + 3}` : ''}
          name={guideLevel.getName()}
          /*
            TODO: Enable for all users once feature is complete
            See https://github.com/albert-io/nexus/issues/7059
          */
          secondaryText={
            sessionStore.isSuper()
              ? `(${guideLevel.getMeta().getContentDiscoveryQuestionCount() || 0})`
              : ''
          }
          indeterminate={guideLevelMap.get(levelId).isIndeterminate}
          description={guideLevel.getDescription()}
          isMatching={guideLevel.getMeta().isMatchesSearch()}
          expanded={expandedVal}
          setExpanded={
            isTheme
              ? (e) => {
                  const newGuideLevelMap = toggleExpandGuideLevel(
                    guideLevel.getId(),
                    guideLevelMap
                  );
                  setGuideLevelMap(newGuideLevelMap);
                  e.stopPropagation();
                }
              : () => {}
          }
          onClick={() => {
            const newGuideLevelMap = hasKeywordSearch
              ? handleKeywordSearchSelection(levelId, guideLevelMap)
              : toggleSelectedGuideLevel(levelId, undefined, guideLevelMap, guideTree);
            setGuideLevelMap(newGuideLevelMap);
            trackEvent({content: guideLevel.getName()});
          }}
          /*
            TODO: Enable for all users once feature is complete
            See https://github.com/albert-io/nexus/issues/7059
          */
          fadeText={
            sessionStore.isSuper() && !guideLevel.getMeta().getContentDiscoveryQuestionCount()
          }
        />
      );
    }, List());
  };

  return (
    <ExpandableResultMemo
      hideMatch={!hasKeywordSearch}
      matches={matchCount}
      name={subject.getName()}
      /*
        TODO: Enable for all users once feature is complete
        See https://github.com/albert-io/nexus/issues/7059
      */
      secondaryText={sessionStore.isSuper() ? `(${guideQuestionCount()})` : ''}
      id={subject.getId()}
      checked={subjectIsSelected}
      indeterminate={subjectIsIndeterminate}
      description={subject.getDescription()}
      expanded={expandSubject}
      onSelect={(e) => {
        // Update this where only if there is a search
        let newGuideLevelMap = guideLevelMap;
        if (hasKeywordSearch) {
          newGuideLevelMap = guideTree.keySeq().reduce((guideMap, guideThemeId) => {
            const {guideLevel} = guideLevelMap.get(guideThemeId);
            if (
              guideLevel.getMeta().isMatchesSearch() ||
              childIsMatch(guideThemeId, guideLevelMap, guideTree)
            ) {
              return handleKeywordSearchSelection(guideThemeId, guideMap);
            }
            return guideMap;
          }, newGuideLevelMap);
          setGuideLevelMap(newGuideLevelMap);
        } else {
          // If a subject is indeterminate - we want to clear it
          const checked = !subjectIsIndeterminate && e.target.checked;
          guideTree.keySeq().forEach((guideThemeId) => {
            newGuideLevelMap = toggleSelectedGuideLevel(
              guideThemeId,
              checked,
              newGuideLevelMap,
              guideTree
            );
          });
          setGuideLevelMap(newGuideLevelMap);
        }
        trackEvent({content: subject.getName()});
      }}
      /*
        TODO: Enable for all users once feature is complete
        See https://github.com/albert-io/nexus/issues/7059
      */
      fadeText={sessionStore.isSuper() && !guideQuestionCount()}
    >
      <div className='u-mar-l_3'>{renderGuideLevels(guide.getSortedGuideLevels())}</div>
    </ExpandableResultMemo>
  );
};
